import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppContext } from "../Contexts";
import { handleOnClickProject } from "../AppBreadcrumb";
import Topbar from "../components/ui/Topbar";
import { getBaseUrl } from "../api/api";
import {
  startWizard,
  getWizardStatus,
  createProject,
  getProject,
  updateProject,
  getWizardStagesByType,
  getWizardSavedFiltersByType,
  getWizardSaleColumnsByType,
  getWizardAutomationsByType,
} from "../api/ProjectApi";
import SelectUser from "./component/SelectUser";
import { fromJS } from "immutable";
import { ItemWrapper } from "./component/ProjectForm";
import InputField from "../components/ui/InputField";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import {
  CurrencyDollarIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserPlusIcon,
  BriefcaseIcon,
  ChartPieIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  BuildingOffice2Icon,
  LifebuoyIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";
import GridTable from "../grid-v2/GridTable";
import CurrencyPicker from "./component/CurrencyPicker";
import ProjectPicker from "./component/ProjectPicker";
import { getTenantId, isJiraContext } from "../api/api";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDot,
  Columns3,
  Dot,
  ListFilter,
  Route,
  X,
} from "lucide-react";
import { renderFieldIcon } from "../fields/FieldList";

export const WORKSPACE_TYPES: {
  value: string;
  label: string;
  description: string;
  Icon: ComponentType<{ className?: string }>;
}[] = [
  {
    value: "SALES",
    label: "Sales",
    description: "Deals, leads, and revenue pipeline",
    Icon: CurrencyDollarIcon,
  },
  {
    value: "PROJECTS",
    label: "Projects",
    description: "Tasks and project tracking",
    Icon: Squares2X2Icon,
  },
  {
    value: "PARTNERSHIPS",
    label: "Partnerships",
    description: "Partner and channel pipeline",
    Icon: UserGroupIcon,
  },
  {
    value: "HIRING",
    label: "Hiring",
    description: "Recruiting and candidates",
    Icon: UserPlusIcon,
  },
  {
    value: "JOB_SEARCH",
    label: "Job search",
    description: "Track your job applications",
    Icon: BriefcaseIcon,
  },
  {
    value: "INVESTOR",
    label: "Investor",
    description: "Investor pipeline and portfolio",
    Icon: ChartPieIcon,
  },
  {
    value: "FUNDRAISING",
    label: "Fundraising",
    description: "Fundraising and deal flow",
    Icon: BanknotesIcon,
  },
  {
    value: "ORDERS",
    label: "Orders",
    description: "Orders and fulfillment",
    Icon: ShoppingCartIcon,
  },
  {
    value: "REAL_ESTATE",
    label: "Real estate",
    description: "Properties and listings",
    Icon: BuildingOffice2Icon,
  },
  {
    value: "SUPPORT",
    label: "Support",
    description: "Support tickets and cases",
    Icon: LifebuoyIcon,
  },
  {
    value: "CUSTOM",
    label: "Custom",
    description: "Build your own pipeline",
    Icon: WrenchScrewdriverIcon,
  },
];

export const WORKSPACE_TYPES_MAP = Object.fromEntries(
  WORKSPACE_TYPES.map(({ value, ...rest }) => [value, rest])
);

const WIZARD_PHASES = [
  "RESEARCHING",
  "STAGES",
  "COLUMNS",
  "VIEWS",
  // "SAMPLE_DATA",
  "AUTOMATION",
];

const PHASE_LABELS: Record<string, string> = {
  RESEARCHING: "Researching your company",
  STAGES: "Creating stages",
  COLUMNS: "Creating columns",
  VIEWS: "Creating views",
  // SAMPLE_DATA: "Creating sample data",
  AUTOMATION: "Setting up automation",
};

const PHASE_DESCRIPTIONS: Record<string, string> = {
  RESEARCHING: "Analyzing your business needs and requirements",
  STAGES: "Designing the workflow stages for your pipeline",
  COLUMNS: "Setting up data fields to track in your pipeline",
  VIEWS: "Building different ways to visualize your data",
  // SAMPLE_DATA: "Populating your pipeline with example entries",
  AUTOMATION: "",
};

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 90000;

export default function PipelineWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appContext = useContext<any>(AppContext);
  const routePrefix = (appContext?.routePrefix ?? "/workspaces") as string;

  const [step, setStep] = useState(1);
  const [workspaceType, setWorkspaceType] = useState<string>("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [projects, setProjects] = useState<any>(fromJS([]));

  const [creating, setCreating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [step3Details, setStep3Details] = useState<{
    stages: string[];
    columns: string[];
    views: { id?: string; name?: string; filters: any }[];
    automations: { id?: string; name?: string; events?: any[]; actions?: any[] }[];
  } | null>(null);

  const [members, setMembers] = useState<any>(fromJS([]));
  const [membersLoading, setMembersLoading] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);

  const currentUser = useSelector((state: any) => state?.userReducer?.get?.("currentUser"));

  const isJira = isJiraContext();
  const workspace = WORKSPACE_TYPES.find((item) => item.value === workspaceType);
  const projectName = name?.trim() || `${workspace?.label || "Sales"} Pipeline`;

  const handleCancel = () => {
    if (step > 1 && (description.trim() || name.trim() || jobId)) {
      if (window.confirm(t("Leave wizard? Your progress will be lost."))) {
        navigate(routePrefix);
      }
    } else {
      navigate(routePrefix);
    }
  };

  const handleNextFrom1 = () => {
    if (workspaceType) setStep(2);
  };

  const handleNextFrom2 = () => {
    setStep(3);
  };

  const handleStartGenerate = () => {
    setError(null);
    setGenerating(true);
    const projectsList = projects?.toJS ? projects.toJS() : (projects ?? []);
    const stageNames = step3Details?.stages || [];
    const saleColumnIds = step3Details?.columns || [];
    const savedFilterIds = step3Details?.views?.map((view) => view.id) || [];
    const automationTemplateIds =
      step3Details?.automations?.map((automation) => automation.id) || [];
    startWizard({
      workspaceType: workspaceType || "SALES",
      description: description.trim() || undefined,
      name: name.trim() || undefined,
      currency: currency || "USD",
      projects: Array.isArray(projectsList) ? projectsList : [],
      generateSampleData: false,
      stageNames,
      saleColumnIds,
      savedFilterIds,
      automationTemplateIds,
    })
      .then(({ jobId: id }) => {
        setJobId(id);
        pollStatus(id);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || t("Failed to start"));
        setGenerating(false);
      });
  };

  const pollStatus = (id: string) => {
    const start = Date.now();
    const run = () => {
      if (Date.now() - start > POLL_TIMEOUT_MS) {
        setError(t("Generation timed out. You can create with default settings."));
        setGenerating(false);
        return;
      }
      getWizardStatus(id)
        .then((res) => {
          if (res.phase === "DONE" && res.workspaceId) {
            setWorkspaceId(res.workspaceId);
            setGenerating(false);
            return;
          }
          if (res.phase === "FAILED") {
            setError(res.error || t("Generation failed"));
            setGenerating(false);
            return;
          }
          setTimeout(run, POLL_INTERVAL_MS);
        })
        .catch((err) => {
          setError(err?.message || t("Status check failed"));
          setGenerating(false);
        });
    };
    run();
  };

  const handleCreateWithDefaults = () => {
    setGenerating(true);
    setError(null);
    const user = currentUser?.toJS ? currentUser.toJS() : currentUser;
    const projectsList = projects?.toJS ? projects.toJS() : (projects ?? []);
    createProject({
      tenantId: getTenantId() || "",
      name: projectName,
      type: workspaceType || "SALES",
      currency: currency || "USD",
      projectManager: user?.accountId ?? "",
      members: user ? [{ ...user, admin: true, active: true }] : [],
      stages: [],
      projects: Array.isArray(projectsList) ? projectsList : [],
    })
      .then((project) => {
        setWorkspaceId(project.id);
        setGenerating(false);
        setStep(4);
      })
      .catch((err) => {
        setError(err?.message || t("Create failed"));
        setGenerating(false);
      });
  };

  const delay = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  useEffect(() => {
    if (step === 2) {
      if (workspace) {
        setName(workspace.label);
        setDescription(workspace.description);
      }
    } else if (step === 3) {
      const getDataByStep = async () => {
        setCreating(true);
        const type = workspace?.value || "SALES";
        setPhase("RESEARCHING");
        await delay(1000);
        const stagesResult = await getWizardStagesByType(type);
        setPhase("STAGES");
        await delay(1000);
        const saleColumnsResult = await getWizardSaleColumnsByType(type);
        setPhase("COLUMNS");
        await delay(1000);
        const savedFiltersResult = await getWizardSavedFiltersByType(type);
        setPhase("VIEWS");
        await delay(1000);
        const automationsResult = await getWizardAutomationsByType(type);
        setPhase("AUTOMATION");
        await delay(1000);
        setStep3Details({
          stages: stagesResult,
          columns: saleColumnsResult,
          views: savedFiltersResult,
          automations: automationsResult,
        });
        setPhase("DONE");
        setCreating(false);
      };
      getDataByStep();
    }
  }, [step, workspace]);

  useEffect(() => {
    if (workspaceId && step === 4) {
      setMembersLoading(true);
      getProject(workspaceId)
        .then((w) => {
          if (w?.members?.length) setMembers(fromJS(w.members));
        })
        .finally(() => setMembersLoading(false));
    }
  }, [workspaceId, step]);

  useEffect(() => {
    if (
      currentUser?.get?.("accountId") &&
      workspaceId &&
      (members.size === 0 || members.length === 0)
    ) {
      setMembers(fromJS([{ ...currentUser.toJS(), admin: true, active: true }]));
    }
  }, [currentUser, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueToWorkspace = () => {
    if (!workspaceId) return;
    setSavingMembers(true);
    const membersList = members.toJS ? members.toJS() : members;
    getProject(workspaceId)
      .then((project) => updateProject({ ...project, members: membersList }))
      .then((project) => {
        setSavingMembers(false);
        handleOnClickProject(project, appContext, navigate);
      })
      .catch(() => setSavingMembers(false));
  };

  const handleSkipToWorkspace = () => {
    if (!workspaceId) return;
    getProject(workspaceId).then((project) => {
      handleOnClickProject(project, appContext, navigate);
    });
  };

  const renderStepper = () => (
    <nav aria-label={t("Progress")}>
      <ol className="flex items-center gap-2 flex-wrap">
        {[t("Type"), t("Profile"), t("Generate"), t("Members")].map((label, index) => {
          const stepNum = index + 1;
          const isCurrent = step === stepNum;
          const isPast = step > stepNum;
          return (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && (
                <span className="text-gray-300" aria-hidden>
                  →
                </span>
              )}
              <span
                className={
                  isCurrent
                    ? "text-sm font-semibold text-blue-600"
                    : isPast
                      ? "text-sm text-gray-500"
                      : "text-sm text-gray-400"
                }
              >
                {stepNum}. {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );

  const renderStep1 = () => (
    <div className="max-w-3xl">
      <h2 className="text-2xl font-semibold text-gray-900 mb-1">
        {t("What will you use this pipeline for?")}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {t("Choose a workspace type to get a tailored setup.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {WORKSPACE_TYPES.map((type) => {
          const selected = workspaceType === type.value;
          return (
            <button
              key={type.value}
              type="button"
              onClick={() => setWorkspaceType(type.value)}
              data-testid={`wizard-type-${type.value}`}
              className={`text-left rounded-lg border-2 p-4 transition flex gap-3 ${
                selected
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
            >
              <span
                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${
                  selected ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                }`}
                aria-hidden
              >
                <type.Icon className="w-6 h-6" />
              </span>
              <span className="min-w-0">
                <div className="font-medium text-gray-900">{type.label}</div>
                <div className="text-sm text-gray-500 mt-0.5">{type.description}</div>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-xl">
      <h2 className="text-2xl font-semibold text-gray-900 mb-1">
        {t("Help us tailor your pipeline (optional)")}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {t("Describe your company or use case so we can customize stages and fields.")}
      </p>
      <ItemWrapper>
        <InputField
          autoFocus
          label={t("Pipeline name")}
          value={name}
          placeholder={t("e.g. Acme Sales") as string}
          onChange={(v) => setName(String(v ?? ""))}
          testId="wizard-name-input"
        />
      </ItemWrapper>
      <ItemWrapper>
        <CurrencyPicker
          currencyCode={currency}
          onChange={(code: string) => setCurrency(code || "USD")}
        />
      </ItemWrapper>
      {isJira && (
        <ItemWrapper>
          <ProjectPicker
            title={t("Jira projects")}
            projects={projects}
            onChange={(selected) => setProjects(fromJS(selected || []))}
            placeholder={t("Search and select Jira projects…") as string}
          />
        </ItemWrapper>
      )}
      <ItemWrapper>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Description")}</label>
        <p className="text-xs text-gray-500 mb-1">
          {t(
            "Company profile – describe your company or use case so we can customize stages and fields."
          )}
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("e.g. B2B SaaS, 50 employees, focus on enterprise deals") as string}
          rows={4}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600"
          data-testid="wizard-profile-input"
          aria-describedby="wizard-description-helper"
        />
        <span id="wizard-description-helper" className="sr-only">
          {t("Company profile used to tailor your pipeline setup.")}
        </span>
      </ItemWrapper>
    </div>
  );

  const renderStep3 = () => (
    <div className="max-w-3xl" role="status" aria-live="polite">
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {step3Details ? t("Review your pipeline") : t("Creating your pipeline...")}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {t("We'll create stages, columns, views, and default automation for you.")}
      </p>

      {error ? (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
          <p className="text-sm text-red-700 mt-1">
            {t("You can create one with default settings and customize it later.")}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleCreateWithDefaults}
              disabled={generating}
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
              data-testid="wizard-create-defaults"
            >
              {t("Create with defaults")}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                handleStartGenerate();
              }}
              disabled={generating}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              {t("Try again")}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 lg:p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{t("Step information")}</h3>
            <div aria-label={t("Generation progress")}>
              {WIZARD_PHASES.map((p, index) => {
                const phaseIndex = WIZARD_PHASES.indexOf(phase || "");
                const currentIndex = WIZARD_PHASES.indexOf(p);
                const isDone = phaseIndex > currentIndex || phase === "DONE";
                const isCurrent = phase === p;
                let stepIcon = (
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full bg-white"></div>
                );
                if (isCurrent) {
                  stepIcon = (
                    <div className="flex items-center justify-center w-6 h-6 border-3 border-blue-600 rounded-full bg-white">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  );
                }
                if (isDone) {
                  stepIcon = (
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full text-white">
                      <Check size={16} />
                    </div>
                  );
                }
                return (
                  <div key={p} className="min-h-20 flex gap-4">
                    <div className="flex flex-col items-center">
                      {stepIcon}
                      {index !== WIZARD_PHASES.length - 1 && (
                        <div
                          className={`w-[2px] flex-1 my-1 ${isDone ? "bg-blue-600" : "bg-gray-300"}`}
                        ></div>
                      )}
                    </div>
                    <div className="pb-4">
                      <h4 className="text-sm font-semibold text-gray-800">{PHASE_LABELS[p]}</h4>
                      <p className="text-sm text-gray-500">{PHASE_DESCRIPTIONS[p]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 lg:p-4">
            <div className="flex items-center gap-2 mb-4">
              {workspace && <workspace.Icon className="w-6 h-6" />}
              <h3 className="text-base font-semibold text-gray-900">{projectName}</h3>
            </div>
            {step3Details ? (
              <div className="px-1 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CircleDot className="w-4 h-4 text-gray-800" />
                    <div className="text-sm font-semibold text-gray-800">{t("Stages")}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(step3Details.stages) && step3Details.stages.length > 0 ? (
                      step3Details.stages.map((stage) => (
                        <div
                          key={stage}
                          className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1"
                        >
                          <div className="text-xs cursor-default">{stage}</div>
                          {!workspaceId && (
                            <button
                              onClick={() => {
                                setStep3Details((prev) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    stages: prev.stages.filter((val) => val !== stage),
                                  };
                                });
                              }}
                              className="text-gray-600 hover:text-gray-700 cursor-pointer"
                            >
                              <X className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">{t("No stages")}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Columns3 className="w-4 h-4 text-gray-800" />
                    <div className="text-sm font-semibold text-gray-800">{t("Columns")}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(step3Details.columns) && step3Details.columns.length > 0 ? (
                      step3Details.columns.map((column) => (
                        <div
                          key={column}
                          className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1"
                        >
                          {renderFieldIcon({ id: column })}
                          <div className="text-xs cursor-default">{column}</div>
                          {!workspaceId && column !== "name" && (
                            <button
                              onClick={() => {
                                setStep3Details((prev) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    columns: prev.columns.filter((val) => val !== column),
                                  };
                                });
                              }}
                              className="text-gray-600 hover:text-gray-700 cursor-pointer"
                            >
                              <X className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">{t("No columns")}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ListFilter className="w-4 h-4 text-gray-800" />
                    <div className="text-sm font-semibold text-gray-800">{t("Views")}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(step3Details.views) && step3Details.views.length > 0 ? (
                      step3Details.views.map((view) => (
                        <div
                          key={view.id}
                          className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1"
                        >
                          <div className="text-xs cursor-default">{view.name}</div>
                          {!workspaceId && (
                            <button
                              onClick={() => {
                                setStep3Details((prev) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    views: prev.views.filter((item) => item.id !== view.id),
                                  };
                                });
                              }}
                              className="text-gray-600 hover:text-gray-700 cursor-pointer"
                            >
                              <X className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">{t("No views")}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Route className="w-4 h-4 text-gray-800" />
                    <div className="text-sm font-semibold text-gray-800">{t("Automations")}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {Array.isArray(step3Details.automations) &&
                    step3Details.automations.length > 0 ? (
                      step3Details.automations.map((automation) => (
                        <div
                          key={automation.id}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2">
                            <Dot className="w-6 h-6" />
                            <div className="text-sm text-gray-600 cursor-default">
                              {automation.name}
                            </div>
                          </div>
                          {!workspaceId && (
                            <button
                              onClick={() => {
                                setStep3Details((prev) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    automations: prev.automations.filter(
                                      (item) => item.id !== automation.id
                                    ),
                                  };
                                });
                              }}
                              className="text-gray-600 hover:text-gray-700 cursor-pointer"
                            >
                              <X className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">{t("No automations")}</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <img
                  src={`${getBaseUrl()}/static/images/deals-view.png`}
                  className="object-contain"
                  alt="Deals view"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => {
    const membersList = members.toJS ? members.toJS() : members;
    const filtered = Array.isArray(membersList) ? membersList : [];
    const rows = filtered.map((m: any) => ({
      user: (
        <div className="flex items-center gap-2">
          {m.avatarUrl ? (
            <img className="h-8 w-8 rounded-full" src={m.avatarUrl} alt="" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-200" />
          )}
          <span className="font-medium text-gray-900">
            {m.displayName || m.email || t("User")}
            {m.admin ? ` (${t("Creator")})` : ""}
          </span>
        </div>
      ),
      role: m.admin ? t("Admin") : t("User"),
      action: (
        <Menu as="div" className="relative">
          <MenuButton className="p-2 text-gray-500 hover:text-gray-900">
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </MenuButton>
          <MenuItems
            anchor="bottom start"
            className="absolute right-0 z-10 mt-1 w-48 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden"
          >
            <MenuItem>
              <button
                type="button"
                onClick={() => {
                  const next = membersList.filter((x: any) => x.accountId !== m.accountId);
                  setMembers(fromJS(next));
                }}
                className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50 data-focus:outline-hidden"
              >
                {t("Remove")}
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      ),
    }));

    return (
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold text-gray-900 mb-1">
          {t("Who should have access?")}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {t("Add members now or skip and do it later from workspace settings.")}
        </p>
        {membersLoading ? (
          <p className="text-sm text-gray-500">{t("Loading…")}</p>
        ) : (
          <>
            <div className="mb-4">
              <SelectUser
                data-testid="wizard-add-member"
                onChange={(user: any) => {
                  const u = user?.value
                    ? {
                        accountId: user.value,
                        displayName: user.label,
                        email: user.email,
                        admin: false,
                        active: true,
                      }
                    : user;
                  if (!u) return;
                  const list = members.toJS ? members.toJS() : members;
                  if (list.some((x: any) => x.accountId === u.accountId)) return;
                  setMembers(fromJS([...list, u]));
                }}
              />
            </div>
            <GridTable
              headers={[
                { key: "user", title: t("User") },
                { key: "role", title: t("Role") },
                { key: "action", title: "" },
              ]}
              rows={rows}
              pageSize={10}
              hidePagination
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white w-full min-h-screen">
      <Topbar
        align="center"
        title={
          <div className="flex items-center justify-center gap-2">
            <img
              alt="Logo"
              src={`${getBaseUrl()}/static/images/logo_gray.png`}
              className="size-8"
            />
            <div className="text-2xl font-semibold text-blue-900">TYPE CRM</div>
          </div>
        }
        sticky
        border
        heightClassName="h-20"
      />
      <div
        className="w-full overflow-y-auto flex justify-center items-start"
        style={{ height: "calc(100vh - 80px)" }}
      >
        <div className="w-full max-w-3xl p-4">
          <div className="flex items-center justify-between gap-2 mt-2 mb-4">
            <div>{renderStepper()}</div>
            <div className="flex items-center justify-end gap-2">
              {step === 1 && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  data-testid="wizard-cancel"
                >
                  {t("Back to workspaces")}
                </button>
              )}
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  data-testid="wizard-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t("Go back")}
                </button>
              )}
              {step === 3 && !workspaceId && !creating && !generating && (
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                    setStep3Details(null);
                    setJobId(null);
                    setError(null);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  data-testid="wizard-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t("Go back")}
                </button>
              )}
              {step === 1 && (
                <button
                  type="button"
                  disabled={!workspaceType}
                  onClick={handleNextFrom1}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-2 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="wizard-next"
                >
                  {t("Next")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {step === 2 && (
                <button
                  type="button"
                  onClick={handleNextFrom2}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-2 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
                  data-testid="wizard-next"
                >
                  {t("Next")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {step === 3 && !workspaceId && (
                <button
                  type="button"
                  disabled={creating || generating}
                  onClick={handleStartGenerate}
                  className="inline-flex items-center rounded-lg bg-blue-600 px-2 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="wizard-start-generate"
                >
                  {generating ? t("Creating your pipeline...") : t("Create pipeline")}
                </button>
              )}
              {step === 3 && workspaceId && !error && (
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="inline-flex items-center rounded-lg bg-blue-600 px-2 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
                  data-testid="wizard-add-members"
                >
                  {t("Continue to add members")}
                </button>
              )}
              {step === 4 && (
                <>
                  <button
                    type="button"
                    onClick={handleSkipToWorkspace}
                    className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {t("Skip")}
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueToWorkspace}
                    disabled={savingMembers}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-2 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="wizard-go-to-workspace"
                  >
                    {savingMembers ? t("Saving…") : t("Go to workspace")}
                  </button>
                </>
              )}
            </div>
          </div>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
      </div>
    </div>
  );
}
